import React, { useRef } from "react";
import {
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  Track,
} from "@vidstack/react";
import {
  defaultLayoutIcons,
  DefaultAudioLayout,
} from "@vidstack/react/player/layouts/default";
import "@vidstack/react/player/styles/default/theme.css";
import "@vidstack/react/player/styles/default/layouts/audio.css";
import styles from "./VidstackAudioPlayer.module.scss";
import { useRouter } from "next/router";
import { FileType } from "@/types/baseTypes";
import { HeaderAudiosType } from "@/types/PageType";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { TranscriptButton } from "../TranscriptButton/TranscriptButton";
import { getVidstackTranslations } from "./VidstackPlayerTranslations";

interface VidstackAudioPlayerProps {
  audio: HeaderAudiosType;
  loop?: boolean;
  singlePage?: boolean;
  singlePageUrl?: string;
  sectionBgColor?: string;
}

const VidstackAudioPlayer = ({
  audio,
  loop = false,
  singlePage = false,
  singlePageUrl,
  sectionBgColor = "",
}: VidstackAudioPlayerProps) => {
  const router = useRouter();
  const player = useRef<MediaPlayerInstance>(null);
  const { getLabel } = useGlobalContext();

  if (!audio || !audio.audio) return null;

  const locale = router.locale ?? router.defaultLocale ?? "de";
  const audioFile = audio.audio as FileType;
  const src = audioFile.url;
  const isHellgrauBg = sectionBgColor === "has-hellgrau-bg";
  const captionsFile = audio.captions ? (audio.captions as FileType) : null;
  const transcriptAriaLabel = getLabel(
    "aria_label_video_player_info_button",
    "zur barrierefreien Version von $$title",
  ).replace("$$title", audio.description || "");

  return (
    <div
      className={
        styles["audio-player-wrapper"] +
        " " +
        (isHellgrauBg ? styles["is-negative"] : "")
      }
      data-audio-player-single-page={singlePage ? true : false}
    >
      <MediaPlayer
        src={src}
        viewType="audio"
        streamType="on-demand"
        autoPlay={false}
        loop={loop}
        crossOrigin
        playsInline
        aria-label={audio.description}
        ref={player}
        title={audio.audio.title || audio.audio.filename || ""}
      >
        {captionsFile && (
          <Track
            kind="captions"
            src={captionsFile.url}
            lang={locale}
            label={audio.description}
          />
        )}

        <MediaProvider />

        <DefaultAudioLayout
          translations={getVidstackTranslations(getLabel)}
          icons={defaultLayoutIcons}
          slots={{
            ...(singlePageUrl && {
              afterSettingsMenu: (
                <TranscriptButton
                  href={singlePageUrl}
                  ariaLabel={transcriptAriaLabel}
                  className={`vds-button ` + styles["vds-button-transcript"]}
                />
              ),
            }),
          }}
        />
      </MediaPlayer>
    </div>
  );
};

export default VidstackAudioPlayer;
